/**
 * Know your competitors — who Mira found, how you compare on authority,
 * links, speed and search, which pages to monitor, and Mira explaining it.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { kyc, type KycDiscovery, type KycRank, type KycSeo, type KycSpeed } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { BenchmarkTable, Favicon, type BenchmarkEntity } from "@/components/kyc/BenchmarkTable";
import { CompetitorList } from "@/components/kyc/CompetitorList";
import { KycMira, MiraHeader } from "@/components/kyc/KycMira";
import { cleanHost } from "@/components/kyc/metrics";
import { Corners } from "@/components/ui/landing-primitives";
import { cn, timeAgo } from "@/lib/utils";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/** The pages that carry the most signal, picked by default (the rest are one click away) */
const DEFAULT_PAGES = ["pricing", "changelog", "features"];
const defaultPages = (suggested: { path: string; label: string }[]) => {
  const best = suggested.filter((p) => DEFAULT_PAGES.includes(p.label)).map((p) => p.path);
  return new Set(best.length ? best : suggested.slice(0, 1).map((p) => p.path));
};

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

const STEPS = ["Reading your product site", "Finding candidates", "Checking each competitor's site"];

export function KycPage() {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const selfDomain = cleanHost(workspace?.domain);
  const isPaid = workspace?.plan === "starter" || workspace?.plan === "growth";

  const [discovery, setDiscovery] = useState<KycDiscovery | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const [ranks, setRanks] = useState<Record<string, KycRank>>({});
  const [speeds, setSpeeds] = useState<Record<string, KycSpeed>>({});
  const [seo, setSeo] = useState<Record<string, KycSeo>>({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsReady, setMetricsReady] = useState(false);
  const isWide = useMediaQuery("(min-width: 1280px)");
  // Mira's side panel can be collapsed on wide screens; remembered per browser
  const [miraOpen, setMiraOpen] = useState(() => {
    try { return localStorage.getItem("kyc-mira-open") !== "0"; } catch { return true; }
  });
  const toggleMira = (open: boolean) => {
    setMiraOpen(open);
    try { localStorage.setItem("kyc-mira-open", open ? "1" : "0"); } catch { /* not persisted */ }
  };

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pages, setPages] = useState<Record<string, Set<string>>>({});
  const [newDomain, setNewDomain] = useState("");
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoRun = useRef(false);
  const discoveryRequestId = useRef(0);
  const metricsRequestId = useRef(0);

  // ── Data ──────────────────────────────────────────────────────────────────

  function loadMetrics() {
    const requestId = ++metricsRequestId.current;
    setMetricsLoading(true);
    void Promise.allSettled([
      kyc.ranks(),
      kyc.speeds(),
      isPaid ? kyc.seo() : Promise.resolve(null),
    ]).then(([ranksResult, speedsResult, seoResult]) => {
      if (requestId !== metricsRequestId.current) return;
      setRanks(ranksResult.status === "fulfilled" && "data" in ranksResult.value && ranksResult.value.data ? ranksResult.value.data : {});
      setSpeeds(speedsResult.status === "fulfilled" && "data" in speedsResult.value && speedsResult.value.data ? speedsResult.value.data : {});
      setSeo(seoResult.status === "fulfilled" && seoResult.value && "data" in seoResult.value && seoResult.value.data ? seoResult.value.data : {});
    }).finally(() => {
      if (requestId === metricsRequestId.current) {
        setMetricsLoading(false);
        setMetricsReady(true);
      }
    });
  }

  function apply(data: KycDiscovery) {
    setDiscovery(data);
    // Saved choices win; competitors never touched start selected with their best pages
    setSelected((prev) => {
      const next = new Set<string>();
      for (const c of data.competitors) {
        const chosen = c.monitor ? c.monitor.selected : prev.size === 0 || prev.has(c.domain) || !discovery?.competitors.some((d) => d.domain === c.domain);
        if (chosen) next.add(c.domain);
      }
      return next;
    });
    setPages((prev) => {
      const next: Record<string, Set<string>> = {};
      for (const c of data.competitors) {
        next[c.domain] = c.monitor ? new Set(c.monitor.pages) : prev[c.domain] ?? defaultPages(c.suggestedPages);
      }
      return next;
    });
    loadMetrics();
  }

  /** Update what's picked for one or more competitors and save it, so a reload keeps it. */
  function choose(changes: { domain: string; selected?: boolean; pages?: Set<string> }[]) {
    const nextSelected = new Set(selected);
    const nextPages = { ...pages };
    for (const ch of changes) {
      if (ch.selected !== undefined) { if (ch.selected) nextSelected.add(ch.domain); else nextSelected.delete(ch.domain); }
      if (ch.pages) nextPages[ch.domain] = ch.pages;
      void kyc.saveMonitor(ch.domain, nextSelected.has(ch.domain), [...(nextPages[ch.domain] ?? [])]).then((res) => {
        if ("error" in res && res.error) toast.error("Couldn't save your selection");
      });
    }
    setSelected(nextSelected);
    setPages(nextPages);
  }

  // Load KYC data when workspace changes
  const loadKycData = useCallback(async () => {
    const requestId = ++discoveryRequestId.current;
    metricsRequestId.current++;
    // Clear all previous workspace data immediately so stale data doesn't show
    setDiscovery(null);
    setRanks({});
    setSpeeds({});
    setSeo({});
    setSelected(new Set());
    setPages({});
    setMetricsReady(false);
    autoRun.current = false;
    setIsDiscovering(false);

    setIsInitialLoading(true);
    const res = await kyc.discovery();
    if (requestId !== discoveryRequestId.current) return;
    const data = "data" in res ? res.data : null;
    if (data) {
      apply(data);
      const age = Date.now() - new Date(data.refreshedAt ?? data.discoveredAt).getTime();
      if (age > STALE_AFTER_MS) {
        setIsRefreshing(true);
        void kyc.refresh().finally(() => {
          if (requestId === discoveryRequestId.current) setIsRefreshing(false);
        });
      }
    } else if (!autoRun.current && selfDomain) {
      autoRun.current = true;
      setIsInitialLoading(false);
      void discover(false);
      return;
    }
    setIsInitialLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id, selfDomain, isPaid]);

  useWorkspaceRefresh(loadKycData);

  async function discover(force: boolean) {
    const requestId = ++discoveryRequestId.current;
    setIsDiscovering(true);
    setStepIdx(0);
    const timers = [setTimeout(() => setStepIdx(1), 5000), setTimeout(() => setStepIdx(2), 12000)];
    const res = await kyc.discover(undefined, force);
    timers.forEach(clearTimeout);
    if (requestId !== discoveryRequestId.current) return;
    setIsDiscovering(false);
    if ("data" in res && res.data) {
      apply(res.data);
      if (!res.data.fromCache) toast.success(`Found ${res.data.competitors.length} competitors`);
    } else {
      toast.error(res.error?.message ?? "Discovery failed");
    }
  }

  async function addCompetitor() {
    const domain = newDomain.trim();
    if (!domain) return;
    setIsAddingCompetitor(true);
    const res = await kyc.addCompetitor(domain);
    setIsAddingCompetitor(false);
    if ("data" in res && res.data && discovery) {
      setNewDomain("");
      toast.success(`Added ${res.data.name}`);
      apply({ ...discovery, competitors: [res.data, ...discovery.competitors] });
    } else {
      toast.error(res.error?.message ?? "Couldn't add that competitor");
    }
  }

  async function removeCompetitor(id: string, name: string) {
    if (!discovery) return;
    setRemovingId(id);
    const res = await kyc.removeCompetitor(id);
    setRemovingId(null);
    if ("data" in res) {
      toast.success(`Removed ${name} — Mira won't suggest it again`);
      apply({ ...discovery, competitors: discovery.competitors.filter((c) => c.id !== id) });
    } else {
      toast.error(res.error?.message ?? "Couldn't remove that competitor");
    }
  }

  async function saveToWatchlist() {
    if (!discovery) return;
    const chosen = discovery.competitors
      .filter((c) => selected.has(c.domain))
      .map((c) => ({ name: c.name, domain: c.domain, pages: c.suggestedPages.filter((p) => pages[c.domain]?.has(p.path)) }))
      .filter((c) => c.pages.length > 0);
    if (!chosen.length) {
      toast.error("Select at least one competitor and page to monitor");
      return;
    }
    setIsSaving(true);
    const res = await kyc.addToWatchlist(chosen);
    setIsSaving(false);
    if ("data" in res && res.data) {
      toast.success(res.data.message);
      navigate("/watchlist");
    } else {
      toast.error(res.error?.message ?? "Couldn't add to watchlist");
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const competitors = discovery?.competitors ?? [];
  const selectedUrlCount = competitors.reduce((n, c) => n + (selected.has(c.domain) ? pages[c.domain]?.size ?? 0 : 0), 0);
  const productName = discovery?.productName || workspace?.name || "Your product";
  const productDomain = cleanHost(discovery?.workspaceDomain) ?? selfDomain;

  const benchSelf: BenchmarkEntity | null = productDomain
    ? { name: productName, domain: productDomain, isSelf: true, rank: ranks[productDomain], speed: speeds[productDomain], seo: seo[productDomain] }
    : null;
  const benchCompetitors: BenchmarkEntity[] = competitors.map((c) => ({
    name: c.name, domain: c.domain, rank: ranks[c.domain], speed: speeds[c.domain], seo: seo[c.domain],
  }));
  // Mira's briefing refreshes when the competitor set changes
  const miraKey = useMemo(() => competitors.map((c) => c.domain).sort().join(","), [competitors]);
  const collapse = isWide ? () => toggleMira(false) : undefined;
  const mira = !isPaid
    ? <MiraLocked onCollapse={collapse} />
    : metricsReady
      ? <KycMira dataKey={miraKey} className="h-full w-full" onCollapse={collapse} />
      : <MiraWaiting onCollapse={collapse} />;

  // ── Render ────────────────────────────────────────────────────────────────

  const header = (
    <PageHeader
      title="Know your competitors"
      actions={discovery ? (
        <>
          {isRefreshing && (
            <span className="hidden items-center gap-1.5 font-mono text-[11px] text-muted-foreground sm:flex">
              <Icon name="refresh" size={10} className="animate-spin" /> Updating
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => discover(true)} disabled={isDiscovering || !productDomain}>
            <Icon name="radar-01" size={12} className="mr-1.5" />Re-discover
          </Button>
          <Button size="sm" onClick={saveToWatchlist} disabled={isSaving || selectedUrlCount === 0}>
            <Icon name={isSaving ? "refresh" : "add-01"} size={12} className={cn("mr-1.5", isSaving && "animate-spin")} />
            Monitor {selectedUrlCount} {selectedUrlCount === 1 ? "page" : "pages"}
          </Button>
        </>
      ) : undefined}
    />
  );

  if (isInitialLoading || (isDiscovering && !discovery)) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-1 items-start p-4 sm:p-6">
          {isDiscovering ? <DiscoverySteps stepIdx={stepIdx} /> : <PageSkeleton />}
        </div>
      </div>
    );
  }

  if (!discovery) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-sm space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-border">
              <Icon name="radar-01" size={20} className="text-muted-foreground/40" />
            </div>
            <div className="space-y-1">
              <p className="font-sans text-sm font-medium">Find the companies you compete with</p>
              <p className="font-sans text-xs text-muted-foreground">
                Mira reads your website, finds candidates, then visits each one and keeps only real competitors.
              </p>
            </div>
            {selfDomain ? (
              <Button size="sm" onClick={() => discover(false)}>
                <Icon name="radar-01" size={12} className="mr-1.5" />Discover competitors
              </Button>
            ) : (
              <Link to="/settings?tab=workspace">
                <Button size="sm" variant="outline"><Icon name="settings-01" size={12} className="mr-1.5" />Set your website first</Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      {header}

      {isDiscovering && (
        <div className="absolute inset-0 top-14 z-20 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <DiscoverySteps stepIdx={stepIdx} />
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <main className="min-w-0 flex-1 space-y-8 overflow-y-auto p-4 sm:p-6">
          {/* ── Your product ── */}
          <section className="relative rounded-lg border border-border bg-card">
            <Corners />
            <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                {workspace?.logoUrl ? (
                  <img src={workspace.logoUrl} alt="" className="h-9 w-9 shrink-0 rounded-md border border-border object-contain p-1" />
                ) : productDomain ? (
                  <Favicon domain={productDomain} name={productName} size={36} />
                ) : null}
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-sans text-sm font-semibold">{productName}</h2>
                    {productDomain && (
                      <a href={`https://${productDomain}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-mono text-[11px] text-muted-foreground hover:text-foreground">
                        {productDomain} <Icon name="arrow-up-right-01" size={11} className="inline-block" />
                      </a>
                    )}
                    {discovery.productCategory && (
                      <span className="rounded-md border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{discovery.productCategory}</span>
                    )}
                  </div>
                  {discovery.productDescription && (
                    <p className="max-w-2xl font-sans text-xs leading-relaxed text-muted-foreground">{discovery.productDescription}</p>
                  )}
                </div>
              </div>
              <p className="shrink-0 font-mono text-[11px] text-muted-foreground">
                Updated {timeAgo(discovery.refreshedAt ?? discovery.discoveredAt)}
              </p>
            </div>
          </section>

          {/* ── How you compare ── */}
          <section className="space-y-3">
            <SectionTitle
              title="How you compare"
              hint={productDomain ? `Hover a column for what it measures.${isPaid ? " Mira explains the gaps." : ""}` : undefined}
            />
            {!productDomain && (
              <p className="font-sans text-xs text-muted-foreground">
                <Link to="/settings?tab=workspace" className="text-foreground underline-offset-2 hover:underline">Add your website</Link> to see your own numbers next to your competitors'.
              </p>
            )}
            <BenchmarkTable self={benchSelf} competitors={benchCompetitors} isPaid={isPaid} isLoading={metricsLoading} />
          </section>

          {/* ── Competitors ── */}
          <section className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-1">
                <SectionTitle title={`Competitors (${competitors.length})`} />
                <p className="font-sans text-[11px] text-muted-foreground">
                  Tick the ones to monitor and the pages to watch — {selectedUrlCount} {selectedUrlCount === 1 ? "page" : "pages"} selected.
                </p>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); void addCompetitor(); }} className="flex items-center gap-2">
                <Input inputSize="sm"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="Add one you know: competitor.com"
                  className="w-full text-xs sm:w-64"
                />
                <Button type="submit" size="sm" variant="outline" disabled={isAddingCompetitor || !newDomain.trim()}>
                  <Icon name={isAddingCompetitor ? "refresh" : "add-01"} size={12} className={cn("mr-1.5", isAddingCompetitor && "animate-spin")} />
                  Add
                </Button>
              </form>
            </div>

            {competitors.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-6 text-center font-sans text-xs text-muted-foreground">
                No competitors yet. Add one you know above, or re-discover.
              </p>
            ) : (
              <CompetitorList
                competitors={competitors}
                selected={selected}
                pages={pages}
                removingId={removingId}
                onToggle={(domain) => choose([{ domain, selected: !selected.has(domain) }])}
                onSetGroup={(domains, on) => choose(domains.map((domain) => ({ domain, selected: on })))}
                onTogglePage={(domain, path) => {
                  const set = new Set(pages[domain] ?? []);
                  if (set.has(path)) set.delete(path); else set.add(path);
                  choose([{ domain, pages: set }]);
                }}
                onRemove={(c) => removeCompetitor(c.id, c.name)}
              />
            )}
          </section>

          {/* Mira sits beside the page on wide screens, below it otherwise (mounted once) */}
          {!isWide && (
            <section className="flex h-[560px] overflow-hidden rounded-lg border border-border bg-card">{mira}</section>
          )}
        </main>

        {isWide && (miraOpen ? (
          <aside className="flex w-[380px] shrink-0 border-l border-border bg-card">{mira}</aside>
        ) : (
          <aside className="flex w-12 shrink-0 flex-col items-center gap-3 border-l border-border bg-card py-2.5">
            <button
              type="button"
              onClick={() => toggleMira(true)}
              title="Show Mira"
              aria-label="Show Mira"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
            >
              <Icon name="panel-right-open" size={15} />
            </button>
            <button
              type="button"
              onClick={() => toggleMira(true)}
              className="flex flex-col items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              <Icon name="magic-wand-01" size={12} />
              <span className="font-mono text-[11px] uppercase tracking-widest [writing-mode:vertical-rl]">Ask Mira</span>
            </button>
          </aside>
        ))}
      </div>
    </div>
  );
}

function MiraLocked({ onCollapse }: { onCollapse?: () => void }) {
  return (
    <div className="flex w-full flex-col">
      <MiraHeader onCollapse={onCollapse} />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border">
          <Icon name="lock" size={16} className="text-muted-foreground" />
        </div>
        <div className="space-y-1.5">
          <p className="font-sans text-sm font-medium">Let Mira explain your numbers</p>
          <p className="font-sans text-xs leading-relaxed text-muted-foreground">
            Mira reads this page and tells you where you trail your competitors, what each score means, and the steps
            that raise your authority, earn more links and speed up your site. Available on Starter and Growth.
          </p>
        </div>
        <Link to="/settings?tab=billing">
          <Button size="sm"><Icon name="flash" size={12} className="mr-1.5" />Upgrade</Button>
        </Link>
      </div>
    </div>
  );
}

function MiraWaiting({ onCollapse }: { onCollapse?: () => void }) {
  return (
    <div className="flex w-full flex-col">
      <MiraHeader onCollapse={onCollapse} />
      <div className="space-y-3 px-5 py-4">
        <p className="font-sans text-xs text-muted-foreground">Measuring authority and page speed — Mira will explain them once they're in.</p>
        {[...Array(4)].map((_, i) => <Skeleton key={i} className={cn("h-3", i % 2 ? "w-2/3" : "w-full")} />)}
      </div>
    </div>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <h2 className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{title}</h2>
      {hint && <p className="hidden font-sans text-[11px] text-muted-foreground/70 md:block">{hint}</p>}
    </div>
  );
}

function DiscoverySteps({ stepIdx }: { stepIdx: number }) {
  return (
    <div className="flex flex-col items-center gap-5">
      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border">
        <Icon name="radar-01" size={20} className="animate-pulse text-muted-foreground" />
      </div>
      <ol className="w-64 space-y-1.5">
        {STEPS.map((label, i) => {
          const done = i < stepIdx;
          const active = i === stepIdx;
          return (
            <li
              key={label}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 transition-all duration-500",
                active ? "bg-foreground/5" : done ? "opacity-50" : "opacity-25",
              )}
            >
              <span className={cn(
                "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full",
                done ? "bg-foreground" : active ? "border-2 border-foreground" : "border border-border",
              )}>
                {done && <Icon name="tick-01" size={8} className="text-background" />}
              </span>
              <span className="font-sans text-xs">{label}</span>
              {active && <Icon name="refresh" size={10} className="ml-auto animate-spin text-muted-foreground" />}
            </li>
          );
        })}
      </ol>
      <p className="font-sans text-[11px] text-muted-foreground">This takes about a minute.</p>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="w-full space-y-6 self-start">
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-56 w-full rounded-lg" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-44 rounded-lg" />)}
      </div>
    </div>
  );
}
